import type { DBFreelancer, FreelancerKind } from "@/lib/db-types";

/**
 * VIB-2925: ren logikk for frilanserbanken (filter, sortering, gruppering,
 * dubletter, lenker). Ingen Supabase her; hookene i use-freelancers.ts
 * henter radene og komponentene viser dem.
 */

export interface FreelancerDisciplineRef {
  id: string;
  label: string;
  sort_order: number;
  is_primary: boolean;
}

export interface FreelancerListRow extends DBFreelancer {
  disciplines: FreelancerDisciplineRef[];
  ratings: number[];
  /** VIB-2927: siste prosjektbruk (bilag eller linje). Settes bare når flagget er på. */
  last_used?: string | null;
}

export interface RatingSummary {
  /** Snitt med én desimal, null uten vurderinger. */
  avg: number | null;
  count: number;
}

export function ratingSummary(ratings: readonly number[]): RatingSummary {
  if (ratings.length === 0) return { avg: null, count: 0 };
  const sum = ratings.reduce((s, r) => s + r, 0);
  return { avg: Math.round((sum / ratings.length) * 10) / 10, count: ratings.length };
}

/**
 * «Testet» = noen hos Sølvrev har vurdert frilanseren, eller (VIB-2927) den er
 * brukt på et prosjekt. Utledes, lagres ikke.
 */
export function isTested(row: Pick<FreelancerListRow, "ratings" | "last_used">): boolean {
  return row.ratings.length > 0 || !!row.last_used;
}

export function primaryDiscipline(row: Pick<FreelancerListRow, "disciplines">): FreelancerDisciplineRef | null {
  return row.disciplines.find((d) => d.is_primary) ?? row.disciplines[0] ?? null;
}

export type TestedFilter = "all" | "tested" | "untested";

export interface FreelancerFilters {
  search: string;
  disciplineIds: string[];
  kind: "all" | FreelancerKind;
  /** 0 = alle. */
  minRating: number;
  tested: TestedFilter;
  showArchived: boolean;
}

export const DEFAULT_FREELANCER_FILTERS: FreelancerFilters = {
  search: "",
  disciplineIds: [],
  kind: "all",
  minRating: 0,
  tested: "all",
  showArchived: false,
};

function searchHaystack(row: FreelancerListRow): string {
  return [row.name, row.company_name, row.location, row.notes, ...row.tags, ...row.disciplines.map((d) => d.label)]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function filterFreelancers(rows: readonly FreelancerListRow[], f: FreelancerFilters): FreelancerListRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (!f.showArchived && row.archived_at) return false;
    if (f.kind !== "all" && row.kind !== f.kind) return false;
    if (f.disciplineIds.length > 0 && !row.disciplines.some((d) => f.disciplineIds.includes(d.id))) return false;
    if (f.tested === "tested" && !isTested(row)) return false;
    if (f.tested === "untested" && isTested(row)) return false;
    if (f.minRating > 0) {
      const { avg } = ratingSummary(row.ratings);
      if (avg == null || avg < f.minRating) return false;
    }
    if (q && !searchHaystack(row).includes(q)) return false;
    return true;
  });
}

export type FreelancerSortKey = "name" | "rating" | "day_rate" | "location" | "last_used";

/** Tall og datoer sorteres synkende først, tekst stigende. */
export function defaultSortDirection(key: FreelancerSortKey): SortDirection {
  return key === "rating" || key === "day_rate" || key === "last_used" ? "desc" : "asc";
}
export type SortDirection = "asc" | "desc";

/** Manglende verdier sorteres alltid sist, uansett retning. */
export function sortFreelancers(
  rows: readonly FreelancerListRow[],
  key: FreelancerSortKey,
  dir: SortDirection,
): FreelancerListRow[] {
  const value = (row: FreelancerListRow): string | number | null => {
    switch (key) {
      case "name":
        return row.name.toLowerCase();
      case "rating":
        return ratingSummary(row.ratings).avg;
      case "day_rate":
        return row.day_rate_nok;
      case "location":
        return row.location?.toLowerCase() || null;
      case "last_used":
        return row.last_used ?? null;
    }
  };
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va == null && vb == null) return a.name.localeCompare(b.name, "nb");
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === "string" && typeof vb === "string") return sign * va.localeCompare(vb, "nb");
    return sign * ((va as number) - (vb as number));
  });
}

export interface FreelancerGroup {
  key: string;
  label: string;
  rows: FreelancerListRow[];
}

/** Gruppér på hovedfagfelt i fagfeltenes rekkefølge, «Uten fagfelt» sist. Navn sortert innad. */
export function groupByPrimaryDiscipline(rows: readonly FreelancerListRow[]): FreelancerGroup[] {
  const groups = new Map<string, FreelancerGroup & { order: number }>();
  for (const row of rows) {
    const p = primaryDiscipline(row);
    const key = p?.id ?? "none";
    if (!groups.has(key)) {
      groups.set(key, { key, label: p?.label ?? "Uten fagfelt", rows: [], order: p?.sort_order ?? Number.MAX_SAFE_INTEGER });
    }
    groups.get(key)!.rows.push(row);
  }
  return [...groups.values()]
    .sort((a, b) => a.order - b.order)
    .map(({ key, label, rows: groupRows }) => ({ key, label, rows: sortFreelancers(groupRows, "name", "asc") }));
}

// ---------------------------------------------------------------------------
// Dubletter
// ---------------------------------------------------------------------------

export function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Siste 8 sifre, så «+47 900 00 000» og «90000000» er samme nummer. */
export function phoneKey(phone: string | null | undefined): string | null {
  const digits = (phone ?? "").replace(/\D/g, "");
  return digits.length >= 8 ? digits.slice(-8) : null;
}

export interface DuplicateCandidate {
  name: string;
  email?: string | null;
  phone?: string | null;
  organization_number?: string | null;
}

export type DuplicateReason = "email" | "phone" | "organization_number" | "name";

export interface DuplicateHit {
  row: DBFreelancer;
  reason: DuplicateReason;
}

export function findDuplicates(
  candidate: DuplicateCandidate,
  rows: readonly DBFreelancer[],
  excludeId?: string,
): DuplicateHit[] {
  const email = candidate.email?.trim().toLowerCase() || null;
  const phone = phoneKey(candidate.phone);
  const org = candidate.organization_number?.replace(/\s/g, "") || null;
  const name = candidate.name.trim() ? normalizeName(candidate.name) : null;
  const hits: DuplicateHit[] = [];
  for (const row of rows) {
    if (row.id === excludeId) continue;
    let reason: DuplicateReason | null = null;
    if (email && row.email?.trim().toLowerCase() === email) reason = "email";
    else if (phone && phoneKey(row.phone) === phone) reason = "phone";
    else if (org && row.organization_number === org) reason = "organization_number";
    else if (name && normalizeName(row.name) === name) reason = "name";
    if (reason) hits.push({ row, reason });
  }
  return hits;
}

// ---------------------------------------------------------------------------
// Lenker og kjennetegn
// ---------------------------------------------------------------------------

/** Gyldig http(s)-lenke med vertsnavn som har punktum. Mangler protokoll, legges https:// til. Ugyldig: null. */
export function normalizeLinkUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withProtocol);
    if (!["http:", "https:"].includes(url.protocol) || !url.hostname.includes(".")) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/** «vimeo.com» fra «https://www.vimeo.com/123». */
export function linkLabelFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export const MAX_TAG_LENGTH = 40;

/** Komma-separert tekst til unike, trimmede kjennetegn. */
export function parseTags(input: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of input.split(",")) {
    const tag = part.trim().slice(0, MAX_TAG_LENGTH);
    const key = tag.toLowerCase();
    if (!tag || seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
}

/** «15 000» eller «15 000 kr» → 15000. Tomt → null. */
export function parseKroner(text: string): number | null {
  const digits = text.replace(/[^\d]/g, "");
  return digits ? Number(digits) : null;
}
